import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { EmptyState, ErrorState, Skeleton, formatCurrency } from "@csa/web-foundation";
import { useT } from "../i18n/index";
import type { MessageKey } from "../i18n/dictionaries";
import { Icon, type IconName } from "../ui/icons";
import { useClaims, useHousehold, usePolicies, useRenewals } from "./use-portfolio";

// CUST-PORTFOLIO-UI — the customer's own portfolio surfaces (N1 policies, N2 household,
// N4 renewals, N5 claims) on the founder's design language: calm surface cards on the DS1
// tokens, status as a semantic pill, money in tabular ink (NEVER alarm-coloured). Read-
// only, masked-only; carrier BRAND names aren't in these reads (carrierId only), so rows
// lead with the line + policy number, never a raw UUID. Empty-states stay reassuring.

type TFn = (k: MessageKey) => string;
type StatusTone = "active" | "warn" | "neutral";

function dateTr(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
function money(v: string | null): string {
  return v ? formatCurrency(v, { currency: "TRY", locale: "tr" }) : "—";
}
// Known lines/assets get a friendly label; anything else falls back to the raw code.
function lineLabel(code: string, t: TFn): string {
  const known: Record<string, MessageKey> = {
    trafik: "port.line.trafik",
    kasko: "port.line.kasko",
  };
  const k = known[code];
  return k ? t(k) : code;
}
function assetLabel(type: string, t: TFn): string {
  const known: Record<string, MessageKey> = {
    VEHICLE: "port.asset.VEHICLE",
    DWELLING: "port.asset.DWELLING",
  };
  const k = known[type];
  return k ? t(k) : type;
}
function lineIcon(code: string): IconName {
  return code === "trafik" || code === "kasko" ? "vehicle" : "vault";
}
function assetIcon(type: string): IconName {
  if (type === "VEHICLE") return "vehicle";
  if (type === "DWELLING") return "dwelling";
  return "vault";
}
// Policy/claim status → a calm tone. ACTIVE reads positive; anything expiring/lapsing is
// a warm amber (a cue, not an alarm); everything else is neutral.
function statusTone(status: string): StatusTone {
  const s = status.toUpperCase();
  if (s === "ACTIVE" || s === "RENEWED") return "active";
  if (/EXPIR|LAPS|DOLUYOR|PENDING/.test(s)) return "warn";
  return "neutral";
}

function StatusPill({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  return (
    <span className={`cx-status cx-status--${tone}`}>
      <span className="cx-status__dot" aria-hidden="true" />
      {children}
    </span>
  );
}

// Shared frame — the page header, then loading skeleton, calm error, graceful empty, cards.
function ListSurface<T>({
  q,
  title,
  sub,
  empty,
  testid,
  layout = "grid",
  children,
}: {
  q: UseQueryResult<T[]>;
  title: MessageKey;
  sub: MessageKey;
  empty: MessageKey;
  testid: string;
  layout?: "grid" | "stack";
  children: (rows: T[]) => ReactNode;
}) {
  const { t } = useT();
  if (q.isLoading)
    return (
      <div className="cx-card">
        <Skeleton lines={4} />
      </div>
    );
  if (q.error)
    return (
      <ErrorState>
        <p data-testid={`${testid}-error`}>{t("port.loadError")}</p>
      </ErrorState>
    );
  const rows = q.data ?? [];
  return (
    <div className="stack" data-testid={testid}>
      <div className="cx-pagehead">
        <div>
          <h1>{t(title)}</h1>
          <p className="cx-pagehead__sub">{t(sub)}</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState>
          <p data-testid={`${testid}-empty`}>{t(empty)}</p>
        </EmptyState>
      ) : (
        <div className={layout === "grid" ? "cx-grid" : "stack"}>{children(rows)}</div>
      )}
    </div>
  );
}

/** N1 — Portföyüm (policies). */
export function Policies() {
  const { t } = useT();
  const q = usePolicies();
  return (
    <ListSurface
      q={q}
      title="port.policies.title"
      sub="port.policies.sub"
      empty="port.policies.empty"
      testid="policies"
    >
      {(rows) =>
        rows.map((p, i) => (
          <article
            key={`${p.carrierPolicyNumber}-${i}`}
            className="cx-card"
            data-testid="policy-row"
          >
            <div className="cx-card__head">
              <span className="cx-tile cx-tile--blue" aria-hidden="true">
                <Icon name={lineIcon(p.lineCode)} size={22} />
              </span>
              <div className="cx-card__headmain">
                <span className="cx-card__title">{lineLabel(p.lineCode, t)}</span>
                <span className="cx-card__sub">
                  {t("port.policyNo")}: {p.carrierPolicyNumber}
                </span>
              </div>
              <StatusPill tone={statusTone(p.status)}>{p.status}</StatusPill>
            </div>
            <div className="cx-metrics">
              <div className="cx-metric">
                <span className="cx-metric__label">{t("port.metric.premium")}</span>
                <span className="cx-metric__value">
                  {money(p.grossPremium)} <span className="muted">{t("port.perYear")}</span>
                </span>
              </div>
              <div className="cx-metric">
                <span className="cx-metric__label">{t("port.metric.renewal")}</span>
                <span className="cx-metric__value">{dateTr(p.termEnd)}</span>
              </div>
              <div className="cx-metric">
                <span className="cx-metric__label">{t("port.metric.term")}</span>
                <span className="cx-metric__value">{dateTr(p.termStart)}</span>
              </div>
            </div>
          </article>
        ))
      }
    </ListSurface>
  );
}

/** N4 — Yenilemeler (renewals). Status is friendly (UPCOMING/RENEWED/LAPSED). */
const RENEWAL_LABEL: Record<string, MessageKey> = {
  UPCOMING: "port.renewal.UPCOMING",
  RENEWED: "port.renewal.RENEWED",
  LAPSED: "port.renewal.LAPSED",
};
export function Renewals() {
  const { t } = useT();
  const q = useRenewals();
  return (
    <ListSurface
      q={q}
      title="port.renewals.title"
      sub="port.renewals.sub"
      empty="port.renewals.empty"
      testid="renewals"
      layout="stack"
    >
      {(rows) =>
        rows.map((r, i) => {
          const key = RENEWAL_LABEL[r.status];
          return (
            <article key={`${r.policyId}-${i}`} className="cx-card" data-testid="renewal-row">
              <div className="cx-card__head">
                <span className="cx-tile cx-tile--muted" aria-hidden="true">
                  <Icon name="calendar" size={22} />
                </span>
                <div className="cx-card__headmain">
                  <span className="cx-card__title">{dateTr(r.termEnd)}</span>
                  <span className="cx-card__sub">
                    {r.daysUntilRenewal} {t("port.daysLeft")}
                  </span>
                </div>
                <StatusPill tone={r.status === "LAPSED" ? "warn" : "active"}>
                  {key ? t(key) : r.status}
                </StatusPill>
              </div>
            </article>
          );
        })
      }
    </ListSurface>
  );
}

/** N5 — Hasarlarım (claims). Money stays calm ink — a claim is service, not alarm. */
export function Claims() {
  const { t } = useT();
  const q = useClaims();
  return (
    <ListSurface
      q={q}
      title="port.claims.title"
      sub="port.claims.sub"
      empty="port.claims.empty"
      testid="claims"
    >
      {(rows) =>
        rows.map((c, i) => (
          <article key={`${c.policyId}-${i}`} className="cx-card" data-testid="claim-row">
            <div className="cx-card__head">
              <span className="cx-tile cx-tile--muted" aria-hidden="true">
                <Icon name="protection" size={22} />
              </span>
              <div className="cx-card__headmain">
                <span className="cx-card__title">{lineLabel(c.lineCode, t)}</span>
                <span className="cx-card__sub">
                  {t("port.lossAt")}: {dateTr(c.lossAt)}
                </span>
              </div>
              <StatusPill tone={statusTone(c.status)}>{c.status}</StatusPill>
            </div>
            {c.outcome || c.outcomeAmount ? (
              <div className="cx-metrics">
                {c.outcomeAmount ? (
                  <div className="cx-metric">
                    <span className="cx-metric__label">{t("port.metric.amount")}</span>
                    <span className="cx-amount">{money(c.outcomeAmount)}</span>
                  </div>
                ) : null}
                {c.outcome ? (
                  <div className="cx-metric">
                    <span className="cx-metric__label">{t("port.metric.status")}</span>
                    <span className="cx-metric__value">{c.outcome}</span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </article>
        ))
      }
    </ListSurface>
  );
}

/** N2 — Hanem (household + insured assets). A single object, not a list. */
export function Household() {
  const { t } = useT();
  const q = useHousehold();
  if (q.isLoading)
    return (
      <div className="cx-card">
        <Skeleton lines={4} />
      </div>
    );
  if (q.error || !q.data)
    return (
      <ErrorState>
        <p data-testid="household-error">{t("port.loadError")}</p>
      </ErrorState>
    );
  const hh = q.data;
  return (
    <div className="stack" data-testid="household">
      <div className="cx-pagehead">
        <div>
          <h1>{hh.name}</h1>
          <p className="cx-pagehead__sub">
            {t("port.household.sub")} · {t("port.household.since")}: {dateTr(hh.ownerSince)}
          </p>
        </div>
      </div>
      {hh.assets.length === 0 ? (
        <EmptyState>
          <p data-testid="household-empty">{t("port.household.empty")}</p>
        </EmptyState>
      ) : (
        <div className="cx-grid">
          {hh.assets.map((a, i) => (
            <article key={i} className="cx-card" data-testid="asset-row">
              <div className="cx-card__head">
                <span className="cx-tile cx-tile--blue" aria-hidden="true">
                  <Icon name={assetIcon(a.assetType)} size={22} />
                </span>
                <div className="cx-card__headmain">
                  <span className="cx-card__title">{a.label ?? assetLabel(a.assetType, t)}</span>
                  {a.plate ? <span className="cx-card__sub">{a.plate}</span> : null}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
